import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import usePageMeta from '@/hooks/usePageMeta';
import { news } from '@/utils/data';
import { getMediaById } from '@/utils/data';

const CATEGORY_LABEL: Record<string, string> = {
  movie: '电影新闻',
  tv: '电视剧新闻',
  review: '影评专题',
};

export default function NewsDetailPage() {
  const { id } = useParams();
  const item = news.find((n) => n.id === id);

  usePageMeta({
    title: item ? `${item.title} - 御宅屋` : '影视资讯 - 御宅屋',
    description: item ? item.summary.slice(0, 120) : '御宅屋影视资讯详情页。',
    keywords: item ? `影视资讯,${item.author},御宅屋资讯` : '影视资讯,御宅屋',
  });

  if (!item) {
    return (
      <div className="w-full px-4 md:px-8 py-24 flex flex-col items-center justify-center text-center">
        <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200/70 text-foreground-500">
          <i className="ri-error-warning-line text-3xl"></i>
        </span>
        <h1 className="mt-5 font-heading text-xl font-bold text-foreground-950">未找到该资讯</h1>
        <p className="mt-2 text-sm text-foreground-500">内容可能已删除或链接有误</p>
        <Link
          to="/news"
          className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i>
          返回资讯列表
        </Link>
      </div>
    );
  }

  const relatedMedia = item.relatedIds.map((rid) => getMediaById(rid)).filter(Boolean);
  const others = news.filter((n) => n.id !== item.id && n.category === item.category).slice(0, 4);

  return (
    <div className="w-full px-4 md:px-8 pb-16">
      <div className="pt-2">
        <Breadcrumb items={[{ label: '影视资讯', to: '/news' }, { label: item.title }]} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
        <article className="lg:col-span-2 min-w-0">
          <div className="rounded-lg overflow-hidden border border-background-200/70 bg-background-100">
            <div className="w-full h-56 md:h-80 overflow-hidden bg-background-200">
              <img
                src={item.cover}
                alt={item.title}
                title={`${item.title} 影视资讯`}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="p-6 md:p-8">
              <span className="inline-block px-2.5 py-1 rounded-md bg-accent-500 text-background-50 text-xs font-semibold">
                {CATEGORY_LABEL[item.category]}
              </span>
              <h1 className="mt-4 font-heading text-2xl md:text-3xl font-bold text-foreground-950 leading-snug">
                {item.title}
              </h1>
              <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-foreground-500 pb-5 border-b border-background-200/70">
                <span className="flex items-center gap-1">
                  <i className="ri-user-3-line"></i>
                  {item.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {item.publishedAt}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  阅读演示数据
                </span>
              </div>

              <div className="mt-6 flex flex-col gap-5">
                {item.content.map((para, index) => (
                  <div key={index}>
                    <p className="text-sm md:text-base text-foreground-700 leading-[2]">{para}</p>
                    {index === 0 && (
                      <figure className="mt-5">
                        <div className="w-full h-52 md:h-72 rounded-lg overflow-hidden border border-background-200/70 bg-background-200">
                          <img
                            src={item.cover}
                            alt={`${item.title} 配图`}
                            title={`${item.title} 影视配图`}
                            className="w-full h-full object-cover object-top"
                          />
                        </div>
                        <figcaption className="mt-2 text-xs text-foreground-500 text-center">
                          图：《{item.title}》相关画面（演示配图）
                        </figcaption>
                      </figure>
                    )}
                    {index === 1 && (
                      <div className="mt-5 rounded-lg border border-background-200/70 bg-background-100 overflow-hidden">
                        <div className="relative w-full h-52 md:h-72 bg-gradient-to-br from-primary-900/70 to-background-200 flex items-center justify-center">
                          <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-50/15 border border-background-50/25 text-foreground-900">
                            <i className="ri-play-fill text-3xl"></i>
                          </span>
                        </div>
                        <div className="px-4 py-3 flex items-center justify-between gap-3 border-t border-background-200/70">
                          <p className="text-xs text-foreground-600">
                            <i className="ri-information-line mr-1 text-accent-400"></i>
                            视频播放为演示占位，本站不提供真实播放功能
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {relatedMedia.length > 0 && (
                <div className="mt-9 pt-6 border-t border-background-200/70">
                  <h2 className="font-heading text-base font-bold text-foreground-950 mb-4 flex items-center gap-2">
                    <span className="w-1 h-4 rounded-full bg-accent-500"></span>
                    相关影视作品
                  </h2>
                  <div className="flex flex-wrap gap-3">
                    {relatedMedia.map(
                      (m) =>
                        m && (
                          <Link
                            key={m.id}
                            to={`/detail/${m.id}`}
                            className="group flex items-center gap-3 px-3 py-2 rounded-lg bg-background-100 border border-background-200/70 hover:border-accent-500/50 transition-colors"
                          >
                            <div className="w-10 h-14 shrink-0 rounded-md overflow-hidden bg-background-200">
                              <img
                                src={m.cover}
                                alt={m.title}
                                title={`${m.title} 影视详情`}
                                className="w-full h-full object-cover object-top"
                              />
                            </div>
                            <div>
                              <p className="text-sm text-foreground-900 group-hover:text-accent-400 transition-colors">
                                {m.title}
                              </p>
                              <p className="text-xs text-foreground-500">
                                {m.year} · {m.genres[0]}
                              </p>
                            </div>
                          </Link>
                        ),
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </article>

        <aside className="min-w-0">
          <div className="rounded-lg bg-background-100 border border-background-200/70 p-5 sticky top-24">
            <h2 className="font-heading text-base font-bold text-foreground-950 mb-4 flex items-center gap-2">
              <span className="w-1 h-4 rounded-full bg-accent-500"></span>
              相关阅读
            </h2>
            <div className="flex flex-col gap-4">
              {others.map((n) => (
                <Link key={n.id} to={`/news/${n.id}`} className="group flex flex-col gap-2">
                  <h3 className="text-sm text-foreground-900 line-clamp-2 group-hover:text-accent-400 transition-colors">
                    {n.title}
                  </h3>
                  <span className="text-xs text-foreground-500">
                    <i className="ri-time-line mr-1"></i>
                    {n.publishedAt}
                  </span>
                </Link>
              ))}
            </div>
            <Link
              to="/news"
              className="mt-5 inline-flex items-center gap-1 text-sm text-accent-400 hover:underline"
            >
              查看全部资讯
              <i className="ri-arrow-right-s-line"></i>
            </Link>
          </div>
        </aside>
      </div>
    </div>
  );
}